import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippy.js/react';
import { Checkbox } from 'sharedComponents';
import { getAssetIcon } from 'helpers/icons';
import { COMPONENT_CONSTANTS } from '../uiConstants';
import './AvailableAssetItem.scss';

/**
 * AvailableAssetItem - Optimized component for rendering individual available assets
 * Uses React.memo for performance with large datasets
 */
const AvailableAssetItem = memo(({
    asset,
    isSelected,
    isDisabled,
    disabledReason,
    onToggle,
    style = {}
}) => {
    const handleClick = () => {
        if (isDisabled) {
            return;
        }
        onToggle(asset);
    };

    const handleKeyDown = (e) => {
        if (isDisabled) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(asset);
        }
    };
    // Determine validation styling
    const getValidationClass = () => {
        if (isDisabled) {
            return 'invalid-asset-type';
        }
        return '';
    };

    return (
        <div style={style} className='available-asset-item-wrapper'>
            <div
                className={`available-asset-item ${isSelected ? 'selected' : ''} ${getValidationClass()}`}
                onClick={handleClick}
                role='button'
                tabIndex={isDisabled ? -1 : 0}
                aria-disabled={isDisabled}
                onKeyDown={handleKeyDown}
            >
                <div className='asset-checkbox'>
                    <Checkbox
                        id={`available-${asset.assetId}`}
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={handleClick}
                    />
                </div>
                <div className='asset-icon-container'>
                    {getAssetIcon(asset.assetType, { ...COMPONENT_CONSTANTS.ASSET_ITEM_ICON_SIZE, color: '#666666' })}
                </div>
                <div className='asset-info'>
                    <div className='asset-name' title={asset.assetName}>
                        {asset.assetName}
                    </div>
                    <div className='asset-id'>
                        ({asset.assetId})
                    </div>
                </div>
                {isDisabled &&
                    <div className='asset-disabled-info'>
                        <Tippy
                            content={disabledReason}
                            interactiveBorder={10}
                            animation='scale'
                            theme='light-border'
                            trigger='click'
                            placement='top'
                            maxWidth={350}
                        >
                            <div className='disabled-icon-wrapper'>
                                <svg className='disabled-info-icon' width='16' height='16' fill='#f59e0b' viewBox='0 0 20 20' aria-hidden='true'>
                                    <path fillRule='evenodd' d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.591c.75 1.334-.213 2.99-1.742 2.99H3.48c-1.53 0-2.492-1.656-1.743-2.99L8.257 3.1zM10 6a.75.75 0 00-.75.75v4.5a.75.75 0 001.5 0v-4.5A.75.75 0 0010 6zm0 8a1 1 0 100-2 1 1 0 000 2z' clipRule='evenodd' />
                                </svg>
                            </div>
                        </Tippy>
                    </div>
                }
            </div>
        </div>
    );
});

AvailableAssetItem.displayName = 'AvailableAssetItem';

AvailableAssetItem.propTypes = {
    asset: PropTypes.shape({
        assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        assetName: PropTypes.string.isRequired,
        assetType: PropTypes.string.isRequired,
        subType: PropTypes.string
    }).isRequired,
    isSelected: PropTypes.bool.isRequired,
    isDisabled: PropTypes.bool,
    disabledReason: PropTypes.string,
    onToggle: PropTypes.func.isRequired,
    style: PropTypes.object
};

export default AvailableAssetItem;
